import { IndexedDBService } from "../../../../services/indexedDB";
import { workerService } from "../../../../services/workerService";
import { simulationService } from "../../../../services/api/simulationService";
import { ToastContainer ,toast } from "react-toastify";
import axiosInstance from "../../../../services/api/axiosInstance";
import { API_ENDPOINTS } from "../../../../services/api/apiConstants";

export const fetchResultsData = async (
  type,
  datasetName,
  baseUrl,
  customerName,
  simulationName,
  token,
) => {

  const cacheKey = `${type}_${datasetName}_${customerName}_${simulationName}`;

  // Guard against calls before required session state is available.
  // After a hard refresh / container restart, sessionStorage may be empty.
  if (!baseUrl) {
    throw new Error("Missing REACT_APP_BASE_URL. Cannot fetch results.");
  }
  if (!token) {
    throw new Error("Not authenticated (missing access token).");
  }
  if (!customerName) {
    throw new Error("Missing storage account (customerName).");
  }
  if (!simulationName) {
    throw new Error("Missing microgrid name (simulationName).");
  }
  if (!datasetName) {
    throw new Error("Missing dataset name (timeseries file).");
  }

  const cachedData = await IndexedDBService.getData(cacheKey);

  if (cachedData) {
   
   
    return cachedData.data;
  }

  const endpoint = "results_csv";
  const queryParams = new URLSearchParams({
    storage_account: customerName,
    microgrid_name: simulationName,
    dataset_name: datasetName,
  });

  const url = `${baseUrl}/storage_manager/${endpoint}?${queryParams}`;
  const headers = {
    Accept: "text/csv",
    Authorization: `Bearer ${token}`,
  };

  try {
    // Prefer axiosInstance for better error details/timeouts.
    // Use a relative URL so axiosInstance.baseURL is applied consistently.
    const queryPath = `${API_ENDPOINTS.STORAGE_MANAGER}/${endpoint}?${queryParams.toString()}`;

    const downloadType =
      process.env.REACT_APP_DOWNLOAD_FILES_STORAGE_TYPE ||
      "AzureStorageManager";

    const response = await axiosInstance.get(queryPath, {
      headers: {
        ...headers,
        "x-input-storage-manager-type": downloadType,
        Accept: "text/plain, text/csv",
      },
      responseType: "text",
      transformResponse: [(data) => data], // avoid JSON parsing
      timeout: 180000,
    });

    if (response?.status >= 200 && response?.status < 300) {
      const csvText = response.data;
      await IndexedDBService.saveData(cacheKey, csvText);
     
      return csvText; // Parsed CSV rows
    } else {
      throw new Error(
        `Failed to fetch ${type} data: ${response?.status || "unknown"} ${response?.statusText || ""}`,
      );
    }
  } catch (error) {
    // Axios provides richer error fields; normalize to a useful message.
    const status = error?.response?.status;
    const details =
      error?.response?.data ||
      error?.message ||
      "Unknown error";
    const printableDetails =
      typeof details === "string" ? details : JSON.stringify(details);
    const baseMsg = `fetchResultsData: Error fetching ${type} data (status=${status || "none"}): ${printableDetails} | url=${url}`;

    // In this Cursor browser tool environment, objects stringify poorly; log a readable message too.
    console.error(baseMsg, error);

    // If we got no HTTP response at all, surface a CORS/offline hint to the UI.
    if (!error?.response) {
      const err = new Error(
        `Network error fetching ${type} data. This is commonly caused by CORS (missing/incorrect Access-Control-Allow-* headers for GET with Authorization) or the backend being unreachable.\nURL: ${url}`,
      );
      err.cause = error;
      throw err;
    }

    throw error;
  }
};

export const parseCSVWithWorker = async (csvText) => {
  try {
    
    
    const startTime = performance.now();

    const result = await workerService.parseCSV(csvText);

    const endTime = performance.now();


    return result;
  } catch (error) {
    // Do NOT fall back to a main-thread Papa.parse here. The results CSV has a
    // two-row header that the worker forward-fills and joins; a naive
    // header:true parse would key rows off row 0 alone and produce a
    // wrong-shaped dataset whose columns no card or chart can find, which then
    // renders as a full screen of confident zeros. Surfacing the failure is
    // strictly better than fabricating data.
    console.error(
      `parseCSVWithWorker: worker CSV parse failed (csvText length=${csvText?.length ?? 0}); no fallback is attempted.`,
      error,
    );
    throw error;
  }
};

export const downloadResultsData = async (
  type,
  datasetName,
  baseUrl,
  customerName,
  simulationName,
  token,
) => {
  const endpoint = type === "Timeseries" ? "results_csv" : "results_csv";

  const queryParams = new URLSearchParams({
    dataset_name: datasetName,
    storage_account: customerName,
    microgrid_name: simulationName,
  });

  const url = `${baseUrl}/storage_manager/${endpoint}?${queryParams}`;

  const headers = {
    "x-input-storage-manager-type":
      process.env.REACT_APP_DOWNLOAD_FILES_STORAGE_TYPE ||
      "InfluxStorageManager",
    Accept: "text/csv",
    Authorization: `Bearer ${token}`,
    "X-User-ID": sessionStorage.getItem("UserID") || "",
  };

 

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: headers,
    });

    if (response.ok) {
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      // Trigger download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = datasetName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    
    } else {
      const errorText = await response.text();
      throw new Error(
        `Failed to download ${type} data: ${response.statusText}, Details: ${errorText}`,
      );
    }
  } catch (error) {
    toast.error("downloadResultsData: Error during download:", error);
    throw error;
  }
};
